import React, { useMemo, useState } from "react";
import { Polyline, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "./RouteLayer.css";

/* ── Settings ────────────────────────────────────────────────────────────── */
// Bend of each segment. The curve peaks at (CURVE / 2) × segment length,
// so 0.22 ≈ 11% of the segment length — a gentle curve.
const ROUTE_CURVE = 0.22;
// Number of straight pieces used to approximate each curved segment.
const ROUTE_SAMPLES = 20;
// Segments shorter than this (in screen pixels) get no arrow (avoids clutter).
const ROUTE_ARROW_MIN_PX = 56;

const mercator = L.Projection.SphericalMercator;

/**
 * Builds the curved route in Web-Mercator space (conformal → the bend looks
 * the same at every zoom level and angles stay correct), then converts the
 * samples back to lat/lng.
 */
function buildRoute(positions) {
  const curve = [];
  const arrows = [];

  for (let i = 0; i < positions.length - 1; i += 1) {
    const from = L.latLng(positions[i]);
    const to = L.latLng(positions[i + 1]);
    const a = mercator.project(from);
    const b = mercator.project(to);

    const dx = b.x - a.x;
    const dy = b.y - a.y;
    if (Math.hypot(dx, dy) === 0) continue; // same coordinates → skip

    // Quadratic Bézier. Control point = midpoint pushed sideways
    // (always to the left of the travel direction → consistent bend).
    const cx = (a.x + b.x) / 2 - dy * ROUTE_CURVE;
    const cy = (a.y + b.y) / 2 + dx * ROUTE_CURVE;

    for (let s = i === 0 ? 0 : 1; s <= ROUTE_SAMPLES; s += 1) {
      const t = s / ROUTE_SAMPLES;
      const u = 1 - t;
      const x = u * u * a.x + 2 * u * t * cx + t * t * b.x;
      const y = u * u * a.y + 2 * u * t * cy + t * t * b.y;
      curve.push(mercator.unproject(L.point(x, y)));
    }

    // Arrow in the middle of the curve (t = 0.5), pointing along the tangent.
    const mx = 0.25 * a.x + 0.5 * cx + 0.25 * b.x;
    const my = 0.25 * a.y + 0.5 * cy + 0.25 * b.y;
    // At t = 0.5 the tangent of a quadratic Bézier equals (b - a).
    // Mercator y points up, screen y points down → negate dy.
    const angle = (Math.atan2(-dy, dx) * 180) / Math.PI;

    arrows.push({
      key: `route-arrow-${i}`,
      from,
      to,
      pos: mercator.unproject(L.point(mx, my)),
      angle,
    });
  }

  return { curve, arrows };
}

const createArrowIcon = (angle) =>
  L.divIcon({
    className: "dap-route-arrow-wrapper",
    html: `<div class="dap-route-arrow" style="transform:rotate(${angle.toFixed(
      1
    )}deg)"><svg class="dap-route-arrow-svg" viewBox="0 0 24 24"><path d="M3 4 L21 12 L3 20 L8 12 Z"/></svg></div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });

/**
 * Drop-in replacement for the straight <Polyline>.
 * Usage inside <MapContainer>:  <RouteLayer positions={positions} />
 */
export default function RouteLayer({ positions }) {
  const map = useMap();
  const [zoom, setZoom] = useState(() => map.getZoom());

  useMapEvents({ zoomend: () => setZoom(map.getZoom()) });

  const route = useMemo(() => buildRoute(positions), [positions]);

  const arrows = useMemo(
    () =>
      route.arrows
        .filter(
          (ar) =>
            map.project(ar.from, zoom).distanceTo(map.project(ar.to, zoom)) >=
            ROUTE_ARROW_MIN_PX
        )
        .map((ar) => ({ ...ar, icon: createArrowIcon(ar.angle) })),
    [route, zoom, map]
  );

  if (route.curve.length < 2) return null;

  return (
    <>
      <Polyline
        positions={route.curve}
        pathOptions={{
          color: "#000000",
          weight: 3,
          opacity: 0.95,
          dashArray: "1 10", // dots (round caps) — period = 11px
          lineCap: "round",
          className: "dap-route-line",
        }}
      />

      {arrows.map((ar) => (
        <Marker
          key={ar.key}
          position={ar.pos}
          icon={ar.icon}
          interactive={false}
          keyboard={false}
          zIndexOffset={-500}
        />
      ))}
    </>
  );
}
